// src/Components/ImportMarkers/ExcelDialogAssignProperty.js

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import DialogActionBar from './DialogActionBar';
import AssignPropertyContent from './AssignPropertyContent';

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

export default function ExcelDialogAssignProperty({ state, actions }) {
  const makeUppercase = state.makeUppercase || false;

  const [pendingMappingValue, setPendingMappingValue] = useState('');
  const [applyToAllRows, setApplyToAllRows] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [selection, setSelection] = useState('');
  const [valueSource, setValueSource] = useState('excel');
  const [staticValue, setStaticValue] = useState('');

  const isDatePickerOpenRef = useRef(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const initialStateRef = useRef({
    assignedTo: '',
    selection: '',
    valueSource: 'excel',
    staticValue: '',
    pendingMappingValue: '',
  });

  const [isOkPressed, setIsOkPressed] = useState(false);

  const submitRef = useRef(null);
  const cancelRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const navUpRef = useRef(null);
  const navDownRef = useRef(null);
  const enterOriginRef = useRef(null);

  const lastGridCellRef = useRef(null);
  const spaceTriggeredRef = useRef(false);

  const forceCalendarFocus = useCallback(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      const target =
        document.querySelector('button.MuiPickersDay-root.Mui-selected') ||
        document.querySelector('button.MuiPickersDay-today') ||
        document.querySelector('button.MuiPickersDay-root');
      if (target) {
        target.focus();
        clearInterval(interval);
        return;
      }
      attempts++;
      if (attempts > 10) clearInterval(interval);
    }, 50);
  }, []);

  const openPicker = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (e && e.stopPropagation) e.stopPropagation();
      isDatePickerOpenRef.current = true;
      setIsDatePickerOpen(true);
      forceCalendarFocus();
    },
    [forceCalendarFocus],
  );

  const closePicker = useCallback(() => {
    isDatePickerOpenRef.current = false;
    setIsDatePickerOpen(false);
  }, []);

  useEffect(() => {
    const trackFocus = (e) => {
      if (e.target && e.target.closest) {
        const cell = e.target.closest(
          'td, th, tr, [role="cell"], [role="gridcell"], [role="row"], .MuiDataGrid-cell, .MuiDataGrid-row',
        );
        if (cell) {
          lastGridCellRef.current = cell;
        }
      }
    };
    window.addEventListener('focusin', trackFocus);
    return () => window.removeEventListener('focusin', trackFocus);
  }, []);

  useEffect(() => {
    if (
      state.invalidMatchState?.open &&
      state.invalidMatchState?.rowId &&
      state.invalidMatchState?.colName
    ) {
      const { rowId, colName } = state.invalidMatchState;

      setTimeout(() => {
        try {
          const rId = String(rowId).replace(/"/g, '\\"');
          const cName = String(colName).replace(/"/g, '\\"');
          const exactCell = document.querySelector(
            `[data-id="${rId}"] [data-field="${cName}"]`,
          );

          if (exactCell && document.activeElement !== exactCell) {
            const active = document.activeElement;
            const inDialog =
              active?.closest('.MuiPickersPopper-root') ||
              active?.closest('#assign-property-panel') ||
              active?.closest('[role="listbox"]') ||
              active?.id === 'custom-val-input';

            if (!inDialog) {
              exactCell.focus({ preventScroll: true });

              const mouseOpts = {
                bubbles: true,
                cancelable: true,
                view: window,
                button: 0,
                buttons: 1,
              };
              exactCell.dispatchEvent(new MouseEvent('mousedown', mouseOpts));

              lastGridCellRef.current = exactCell;
            }
          }
        } catch (e) {}
      }, 80);
    }
  }, [state.invalidMatchState]);

  const restoreGridFocus = useCallback(() => {
    const activeEl = document.activeElement;
    if (activeEl && activeEl.tagName !== 'BODY') {
      activeEl.blur();
    }

    const rowId = state.invalidMatchState?.rowId;
    const colName = state.invalidMatchState?.colName;

    setTimeout(() => {
      let focused = false;

      if (rowId && colName) {
        try {
          const rId = String(rowId).replace(/"/g, '\\"');
          const cName = String(colName).replace(/"/g, '\\"');
          const exactCell = document.querySelector(
            `[data-id="${rId}"] [data-field="${cName}"]`,
          );

          if (exactCell) {
            exactCell.focus({ preventScroll: true });

            const mouseOpts = {
              bubbles: true,
              cancelable: true,
              view: window,
              button: 0,
              buttons: 1,
            };
            exactCell.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
            exactCell.dispatchEvent(new MouseEvent('mouseup', mouseOpts));
            exactCell.dispatchEvent(new MouseEvent('click', mouseOpts));

            lastGridCellRef.current = exactCell;
            focused = true;
          }
        } catch (e) {}
      }

      if (
        !focused &&
        lastGridCellRef.current &&
        document.contains(lastGridCellRef.current)
      ) {
        lastGridCellRef.current.focus({ preventScroll: true });

        const mouseOpts = {
          bubbles: true,
          cancelable: true,
          view: window,
          button: 0,
          buttons: 1,
        };
        lastGridCellRef.current.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
        focused = true;
      }

      if (!focused) {
        const grid = document.querySelector('.MuiDataGrid-root, [role="grid"]');
        if (grid) {
          grid.focus({ preventScroll: true });
          focused = true;
        }
      }

      if (!focused) {
        document.body.focus();
      }
    }, 150);
  }, [state.invalidMatchState]);

  const sortedIcons = useMemo(() => {
    return state.markerIcons
      ? [...state.markerIcons]
          .filter((icon) => !icon.isArchived)
          .sort((a, b) => {
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;
            return (a.name || '').localeCompare(b.name || '');
          })
      : [];
  }, [state.markerIcons]);

  useEffect(() => {
    if (state.invalidMatchState?.open) {
      const { colName, type, rowId, cellValue } = state.invalidMatchState;
      setPendingMappingValue('');
      setApplyToAllRows(false);
      closePicker();

      if (type === 'REGION' || type === 'CATEGORY' || type === 'ICON') {
        const targetRow = state.parsedData?.find((r) => r.id === rowId);
        const isCurrentlyManuallyAssigned = targetRow?._manuallyAssigned?.[colName];
        let initVal = '';

        if (type === 'REGION') {
          if (isCurrentlyManuallyAssigned) {
            const actualKey = Object.keys(targetRow).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            const rObj = state.regions?.find((r) => r.name === targetRow[actualKey]);
            initVal = rObj ? rObj.id : '';
          } else {
            const rObj = state.regions?.find(
              (r) => normalizeLooseString(r.name) === normalizeLooseString(cellValue),
            );
            initVal = rObj ? rObj.id : '';
          }
        } else if (type === 'CATEGORY') {
          if (isCurrentlyManuallyAssigned) {
            const actualKey = Object.keys(targetRow).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            const cObj = state.categories?.find((c) => c.name === targetRow[actualKey]);
            initVal = cObj ? cObj.id : '';
          } else {
            const cObj = state.categories?.find(
              (c) => normalizeLooseString(c.name) === normalizeLooseString(cellValue),
            );
            initVal = cObj ? cObj.id : '';
          }
        } else if (type === 'ICON') {
          if (isCurrentlyManuallyAssigned) {
            const actualKey = Object.keys(targetRow).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            initVal = targetRow[actualKey];
          } else {
            const cleanCell =
              cellValue !== undefined && cellValue !== null
                ? String(cellValue).trim()
                : '';
            let iObj = null;
            if (cleanCell !== '') {
              iObj = state.markerIcons?.find(
                (i) =>
                  normalizeLooseString(i.name) !== '' &&
                  normalizeLooseString(i.name) === normalizeLooseString(cleanCell),
              );
            }
            if (iObj) {
              initVal = iObj.id;
            } else {
              const def = state.markerIcons?.find((i) => i.isDefault === true);
              initVal = def ? def.id : '';
            }
          }
        }

        setPendingMappingValue(initVal);
        setAssignedTo('');
        setSelection('');
        setStaticValue('');
        setValueSource('excel');

        initialStateRef.current = {
          assignedTo: '',
          selection: '',
          valueSource: 'excel',
          staticValue: '',
          pendingMappingValue: initVal,
        };
        return;
      }

      if (type === 'TEXT' || type === 'TEMPLATE_MISMATCH' || type === 'DESCRIPTION') {
        const targetRow = state.parsedData?.find((r) => r.id === rowId);
        if (targetRow && targetRow._localMappings && targetRow._localMappings[colName]) {
          const local = targetRow._localMappings[colName];
          const locAssigned = local.assignedTo ? local.assignedTo.toUpperCase() : '';
          let locSel = local.selection ? local.selection.toUpperCase() : '';

          let localStatic =
            local.staticValue !== null && local.staticValue !== undefined
              ? local.staticValue
              : cellValue || '';

          let defaultedDate = false;
          if (locAssigned === 'VISIBILITY') {
            if (!locSel) {
              const upperCell = String(cellValue || '')
                .toUpperCase()
                .trim();
              if (upperCell.startsWith('PUBLIC FROM')) locSel = 'PUBLIC FROM';
              else if (upperCell === 'RESTRICTED') locSel = 'RESTRICTED';
              else if (upperCell === 'PRIVATE') locSel = 'PRIVATE';
              else locSel = 'PUBLIC';
            }
            if (locSel === 'PUBLIC FROM') {
              localStatic = String(localStatic)
                .replace(/^PUBLIC FROM\s+/i, '')
                .trim();
              if (!localStatic && state.defaultPublicFromDate) {
                localStatic = state.defaultPublicFromDate;
                defaultedDate = true;
              }
            }
          }

          setAssignedTo(locAssigned);
          setSelection(locSel);

          const applyCase = (val) =>
            makeUppercase && typeof val === 'string' ? val.toUpperCase() : val;

          if (type === 'TEMPLATE_MISMATCH') {
            setValueSource('custom');
            setStaticValue(applyCase(localStatic));
            initialStateRef.current = {
              assignedTo: locAssigned,
              selection: locSel,
              valueSource: 'custom',
              staticValue: applyCase(localStatic),
              pendingMappingValue: '',
            };
          } else {
            if (
              (localStatic !== null && localStatic !== undefined && localStatic !== '') ||
              defaultedDate
            ) {
              setStaticValue(applyCase(localStatic));
              setValueSource('custom');
              initialStateRef.current = {
                assignedTo: locAssigned,
                selection: locSel,
                valueSource: 'custom',
                staticValue: applyCase(localStatic),
                pendingMappingValue: '',
              };
            } else {
              setStaticValue('');
              setValueSource('excel');
              initialStateRef.current = {
                assignedTo: locAssigned,
                selection: locSel,
                valueSource: 'excel',
                staticValue: '',
                pendingMappingValue: '',
              };
            }
          }
          return;
        }

        let foundAssignedTo = '';
        let foundSelection = '';
        let foundStaticValue = '';
        let matchedKey = null;

        const rawColName = normalizeLooseString(colName);
        const searchKeyProp = `prop_${rawColName}`;

        matchedKey = Object.keys(state.rowMappings || {}).find(
          (k) =>
            normalizeLooseString(k) === searchKeyProp ||
            normalizeLooseString(k) === rawColName,
        );

        if (!matchedKey && state.customRows) {
          const matchedCustomRow = state.customRows.find(
            (r) => normalizeLooseString(r.name) === rawColName,
          );
          if (matchedCustomRow) {
            matchedKey = matchedCustomRow.id;
          }
        }

        if (matchedKey) {
          foundAssignedTo = state.rowMappings?.[matchedKey] || '';
          foundSelection = state.rowSubMappings?.[matchedKey] || '';
          foundStaticValue = state.rowStaticValues?.[matchedKey] || '';
        }

        const fAssigned = foundAssignedTo ? foundAssignedTo.toUpperCase() : '';
        let fSel = foundSelection ? foundSelection.toUpperCase() : '';

        let finalStatic = foundStaticValue || cellValue || '';
        let defaultedGlobalDate = false;

        // AMENDED: Parse the actual cell value to restore correct Visibility radio and date state
        if (fAssigned === 'VISIBILITY') {
          const upperCell = String(cellValue || '')
            .toUpperCase()
            .trim();
          if (upperCell.startsWith('PUBLIC FROM')) {
            fSel = 'PUBLIC FROM';
            finalStatic = String(cellValue)
              .replace(/^PUBLIC FROM\s+/i, '')
              .trim();
          } else if (upperCell === 'RESTRICTED') {
            fSel = 'RESTRICTED';
            finalStatic = '';
          } else if (upperCell === 'PRIVATE') {
            fSel = 'PRIVATE';
            finalStatic = '';
          } else if (upperCell === 'PUBLIC') {
            fSel = 'PUBLIC';
            finalStatic = '';
          }

          if (!fSel) {
            fSel = 'PUBLIC';
          }

          if (fSel === 'PUBLIC FROM') {
            if (!finalStatic && state.defaultPublicFromDate) {
              finalStatic = state.defaultPublicFromDate;
              defaultedGlobalDate = true;
            }
          }
        }

        setAssignedTo(fAssigned);
        setSelection(fSel);

        const applyCase = (val) =>
          makeUppercase && typeof val === 'string' ? val.toUpperCase() : val;

        if (type === 'TEMPLATE_MISMATCH') {
          setValueSource('custom');
          setStaticValue(applyCase(finalStatic));
          initialStateRef.current = {
            assignedTo: fAssigned,
            selection: fSel,
            valueSource: 'custom',
            staticValue: applyCase(finalStatic),
            pendingMappingValue: '',
          };
        } else {
          // Treat 'PUBLIC FROM' dates as custom source because the date relies on user specification
          if (
            (finalStatic !== null && finalStatic !== undefined && finalStatic !== '') ||
            defaultedGlobalDate ||
            (fAssigned === 'VISIBILITY' && fSel === 'PUBLIC FROM')
          ) {
            setStaticValue(applyCase(finalStatic));
            setValueSource('custom');
            initialStateRef.current = {
              assignedTo: fAssigned,
              selection: fSel,
              valueSource: 'custom',
              staticValue: applyCase(finalStatic),
              pendingMappingValue: '',
            };
          } else {
            setStaticValue('');
            setValueSource('excel');
            initialStateRef.current = {
              assignedTo: fAssigned,
              selection: fSel,
              valueSource: 'excel',
              staticValue: '',
              pendingMappingValue: '',
            };
          }
        }
      } else {
        setAssignedTo('');
        setSelection('');
        setStaticValue('');
        setValueSource('excel');
        initialStateRef.current = {
          assignedTo: '',
          selection: '',
          valueSource: 'excel',
          staticValue: '',
          pendingMappingValue: '',
        };
      }
    }
  }, [
    state.invalidMatchState,
    state.rowMappings,
    state.rowSubMappings,
    state.rowStaticValues,
    state.customRows,
    state.parsedData,
    state.categories,
    state.markerIcons,
    state.regions,
    state.defaultPublicFromDate,
    makeUppercase,
    closePicker,
  ]);

  const handleValidationOkSubmit = (goToNext = false) => {
    setIsDatePickerOpen(false);
    const { type, colName, cellValue, rowId } = state.invalidMatchState || {};

    let newValueName = '';
    const isThreeTierFlow = type === 'TEXT' || type === 'TEMPLATE_MISMATCH';

    const isBooleanField =
      assignedTo === 'INFO' && (selection === 'SHOWCASE' || selection === 'HILIGHT');

    if (type === 'REGION') {
      newValueName =
        state.regions?.find((x) => String(x.id) === String(pendingMappingValue))?.name ||
        cellValue;
    } else if (type === 'CATEGORY') {
      newValueName =
        state.categories?.find((x) => String(x.id) === String(pendingMappingValue))
          ?.name || cellValue;
    } else if (type === 'ICON') {
      newValueName = pendingMappingValue;
    }

    let autoCategoryName = null;
    let catColName = null;

    if (type === 'ICON' && pendingMappingValue) {
      const selectedIcon = state.markerIcons?.find(
        (x) => String(x.id) === String(pendingMappingValue),
      );
      if (selectedIcon && selectedIcon.categoryId) {
        const matchedCat = state.categories?.find(
          (c) => String(c.id) === String(selectedIcon.categoryId),
        );
        if (matchedCat && matchedCat.name) {
          autoCategoryName = matchedCat.name;
        }
      }

      const catColKey = Object.keys(state.rowMappings || {}).find(
        (k) =>
          state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'CATEGORY',
      );
      if (catColKey) {
        const cRow = state.customRows?.find((r) => r.id === catColKey);
        if (cRow && cRow.name !== '-') catColName = cRow.name;
        else if (catColKey.startsWith('prop_')) catColName = catColKey.substring(5);
      }
    }

    const matchVal = (val1, val2) => {
      const v1 =
        val1 !== undefined && val1 !== null ? String(val1).trim().toLowerCase() : '';
      const v2 =
        val2 !== undefined && val2 !== null ? String(val2).trim().toLowerCase() : '';
      return v1 === v2;
    };

    actions.setParsedData?.((prev) =>
      prev.map((row) => {
        let shouldApply = false;

        const actualRowKey = Object.keys(row).find(
          (k) => normalizeLooseString(k) === normalizeLooseString(colName),
        );
        const currentRowValue = actualRowKey ? row[actualRowKey] : undefined;

        if (row.id === rowId) {
          shouldApply = true;
        } else if (applyToAllRows) {
          if (isThreeTierFlow || type === 'ICON') {
            shouldApply = true;
          } else {
            if (matchVal(currentRowValue, cellValue)) {
              shouldApply = true;
            }
          }
        }

        if (shouldApply) {
          const newRow = { ...row };
          const assignKey = actualRowKey || colName;

          if (isThreeTierFlow) {
            let activeStaticValue = staticValue;
            let activeValueSource = valueSource;

            const customInputEl = document.getElementById('custom-val-input');
            if (customInputEl && customInputEl.type === 'date') {
              activeStaticValue = customInputEl.value;
              if (activeStaticValue) activeValueSource = 'custom';
            }

            if (
              isBooleanField &&
              (!activeStaticValue || String(activeStaticValue).trim() === '')
            ) {
              activeStaticValue = 'false';
            }

            let activeExcelValue = currentRowValue !== undefined ? currentRowValue : '';
            if (
              isBooleanField &&
              (!activeExcelValue || String(activeExcelValue).trim() === '')
            ) {
              activeExcelValue = 'false';
            }

            let finalValue =
              activeValueSource === 'custom' ? activeStaticValue : activeExcelValue;

            if (assignedTo === 'VISIBILITY') {
              if (selection === 'PUBLIC FROM') {
                const cleanDate = String(finalValue || '')
                  .replace(/^PUBLIC FROM\s+/i, '')
                  .trim();
                activeStaticValue = cleanDate
                  ? `PUBLIC FROM ${cleanDate}`
                  : 'PUBLIC FROM';
              } else {
                activeStaticValue = selection;
              }
              finalValue = activeStaticValue;
            }

            newRow[assignKey] = finalValue;

            newRow._manuallyAssigned = {
              ...(row._manuallyAssigned || {}),
              [colName]: true,
            };

            newRow._localMappings = {
              ...(row._localMappings || {}),
              [colName]: {
                assignedTo: assignedTo || '',
                selection: selection || '',
                staticValue:
                  assignedTo === 'VISIBILITY'
                    ? activeStaticValue
                    : activeValueSource === 'custom'
                      ? activeStaticValue
                      : null,
              },
            };
          } else {
            const isClearingTwoTier = !pendingMappingValue;
            newRow[assignKey] = isClearingTwoTier ? cellValue : newValueName;
            newRow._manuallyAssigned = {
              ...(row._manuallyAssigned || {}),
              [colName]: !isClearingTwoTier,
            };

            if (type === 'ICON' && autoCategoryName && catColName && !isClearingTwoTier) {
              const actualCatKey =
                Object.keys(newRow).find(
                  (k) => normalizeLooseString(k) === normalizeLooseString(catColName),
                ) || catColName;

              const currentCatVal = newRow[actualCatKey];
              const cellHasCatData =
                currentCatVal !== undefined &&
                currentCatVal !== null &&
                String(currentCatVal).trim() !== '';

              if (!cellHasCatData) {
                newRow[actualCatKey] = autoCategoryName;
                newRow._manuallyAssigned = {
                  ...(newRow._manuallyAssigned || {}),
                  [actualCatKey]: true,
                };
              }
            }
          }
          return newRow;
        }
        return row;
      }),
    );

    if (goToNext) {
      if (actions.setPendingNextColumn) {
        actions.setPendingNextColumn({ rowId, colName });
      }
    }

    setPendingMappingValue('');
    setAssignedTo('');
    setSelection('');
    setStaticValue('');
    setValueSource('excel');

    restoreGridFocus();
  };

  const handleValidationCancelClick = () => {
    const init = initialStateRef.current;

    if (state.invalidMatchState?.type === 'ICON') {
      const defaultIcon =
        state.markerIcons?.find((i) => i.isDefault === true) || sortedIcons[0];
      const defaultIconId = defaultIcon ? defaultIcon.id : '';
      const tray = document.getElementById('icon-tray-container');

      if (pendingMappingValue !== defaultIconId && defaultIconId) {
        setPendingMappingValue(defaultIconId);
        setTimeout(() => {
          const el = document.getElementById(`icon-item-${defaultIconId}`);
          if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          if (tray) tray.focus();
        }, 50);
        return;
      } else {
        handleValidationCancel();
        return;
      }
    }

    if (
      assignedTo !== init.assignedTo ||
      selection !== init.selection ||
      staticValue !== init.staticValue ||
      valueSource !== init.valueSource ||
      pendingMappingValue !== init.pendingMappingValue
    ) {
      setAssignedTo(init.assignedTo);
      setSelection(init.selection);
      setStaticValue(init.staticValue);
      setValueSource(init.valueSource);
      setPendingMappingValue(init.pendingMappingValue);

      restoreGridFocus();
    } else {
      handleValidationCancel();
    }
  };

  const handleValidationCancel = () => {
    setIsDatePickerOpen(false);
    setPendingMappingValue('');
    setAssignedTo('');
    setSelection('');
    setStaticValue('');
    setValueSource('excel');
    actions.setInvalidMatchState?.({
      open: false,
      rowId: null,
      colName: null,
      cellValue: '',
      type: null,
    });

    restoreGridFocus();
  };

  const handleNavPrev = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        direction: 'prev',
      });
    }
  };

  const handleNavNext = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        direction: 'next',
      });
    }
  };

  const handleNavUp = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        rowDirection: 'up',
      });
    }
  };

  const handleNavDown = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        rowDirection: 'down',
      });
    }
  };

  const isUnspecified = state.invalidMatchState?.type === 'TEXT';
  const isTemplateMismatch = state.invalidMatchState?.type === 'TEMPLATE_MISMATCH';
  const showThreeTierLayout = isUnspecified || isTemplateMismatch;
  const isOkDisabled = isUnspecified;

  useEffect(() => {
    submitRef.current = () => {
      if (state.invalidMatchState?.type === 'TEXT') return;
      handleValidationOkSubmit(false);
    };
    cancelRef.current = handleValidationCancelClick;
    navPrevRef.current = handleNavPrev;
    navNextRef.current = handleNavNext;
    navUpRef.current = handleNavUp;
    navDownRef.current = handleNavDown;
  });

  useEffect(() => {
    const isUnspecifiedLocal = state.invalidMatchState?.type === 'TEXT';
    const isTemplateMismatchLocal = state.invalidMatchState?.type === 'TEMPLATE_MISMATCH';
    const showThreeTierLayoutLocal = isUnspecifiedLocal || isTemplateMismatchLocal;
    const isDateFieldLocal = assignedTo === 'INFO' && selection === 'DATE';

    const isDateInputRendered =
      (assignedTo === 'VISIBILITY' && selection === 'PUBLIC FROM') ||
      (showThreeTierLayoutLocal &&
        isTemplateMismatchLocal &&
        assignedTo !== 'VISIBILITY' &&
        isDateFieldLocal) ||
      (showThreeTierLayoutLocal &&
        isUnspecifiedLocal &&
        assignedTo !== 'VISIBILITY' &&
        valueSource === 'custom' &&
        isDateFieldLocal);

    const handleGlobalKeyDown = (e) => {
      if (!state.invalidMatchState?.open) return;

      const activeEl = document.activeElement;

      const inSidePanel = !!(activeEl && activeEl.closest('#assign-property-panel'));

      const isTextInputReal =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable);

      const isControl =
        inSidePanel &&
        (activeEl.tagName === 'BUTTON' ||
          activeEl.type === 'radio' ||
          activeEl.type === 'checkbox' ||
          activeEl.getAttribute('role') === 'combobox' ||
          activeEl.classList?.contains('MuiSelect-select') ||
          activeEl.getAttribute('role') === 'menuitem' ||
          activeEl.getAttribute('role') === 'option' ||
          activeEl.getAttribute('role') === 'switch');

      if (isDatePickerOpenRef.current) return;

      const isTrayFocused =
        activeEl &&
        (activeEl.id === 'icon-tray-container' ||
          (activeEl.closest && activeEl.closest('#icon-tray-container')));
      const isListBox = document.querySelector('[role="listbox"]');

      if (e.key === 'Escape') {
        if (isListBox) return;

        if (inSidePanel || isTrayFocused) {
          e.preventDefault();
          e.stopPropagation();
          restoreGridFocus();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        if (cancelRef.current) cancelRef.current();
        return;
      }

      if (isListBox) {
        if (e.key === 'Enter') enterOriginRef.current = 'listbox';
        return;
      }

      if (
        isTrayFocused &&
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        let currentIndex = sortedIcons.findIndex((i) => i.id === pendingMappingValue);
        if (currentIndex === -1) currentIndex = 0;

        let nextIndex = currentIndex;
        if (e.key === 'ArrowRight') nextIndex = currentIndex + 1;
        if (e.key === 'ArrowLeft') nextIndex = currentIndex - 1;
        if (e.key === 'ArrowDown') nextIndex = currentIndex + 5;
        if (e.key === 'ArrowUp') nextIndex = currentIndex - 5;

        if (nextIndex < 0) nextIndex = 0;
        if (nextIndex >= sortedIcons.length) nextIndex = sortedIcons.length - 1;

        setPendingMappingValue(sortedIcons[nextIndex].id);

        const nextId = sortedIcons[nextIndex].id;
        const el = document.getElementById(`icon-item-${nextId}`);
        if (el) {
          el.focus({ preventScroll: true });
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        return;
      }

      const openMuiSelect = (el) => {
        if (!el) return;
        el.focus();
        const event = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window,
          button: 0,
          buttons: 1,
        });
        el.dispatchEvent(event);
      };

      if (!isTextInputReal && !isControl && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        e.stopPropagation();
        spaceTriggeredRef.current = true;

        const { type } = state.invalidMatchState;
        if (type === 'REGION' || type === 'CATEGORY') {
          openMuiSelect(document.getElementById('mapping-select-trigger'));
        } else if (type === 'ICON') {
          if (document.activeElement && document.activeElement.tagName !== 'BODY') {
            document.activeElement.blur();
          }

          let attempts = 0;
          const focusInterval = setInterval(() => {
            const currentId =
              pendingMappingValue || (sortedIcons.length > 0 ? sortedIcons[0].id : null);

            if (currentId) {
              const selectedEl = document.getElementById(`icon-item-${currentId}`);
              if (selectedEl) {
                selectedEl.focus({ preventScroll: true });
                if (document.activeElement === selectedEl) {
                  clearInterval(focusInterval);
                  selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                  return;
                }
              }
            }
            const tray = document.getElementById('icon-tray-container');
            if (tray) {
              tray.focus({ preventScroll: true });
              if (document.activeElement === tray) {
                clearInterval(focusInterval);
                return;
              }
            }
            attempts++;
            if (attempts > 15) {
              clearInterval(focusInterval);
            }
          }, 50);
        } else if (type === 'TEMPLATE_MISMATCH') {
          const assignInput = document.getElementById('assigned-to-select');
          const selectionInput = document.getElementById('selection-select');

          if (!assignedTo && assignInput) {
            openMuiSelect(assignInput);
          } else if (assignedTo === 'INFO' && !selection && selectionInput) {
            openMuiSelect(selectionInput);
          } else if (isDateInputRendered) {
            openPicker(e);
          } else if (assignedTo === 'VISIBILITY') {
            const checkedRadio =
              document.querySelector('input[name="visibility-radios"]:checked') ||
              document.querySelector('input[name="visibility-radios"]');
            if (checkedRadio) checkedRadio.focus();
          } else {
            const focusCustomInput = () => {
              const tryFocus = () => {
                const valInput = document.getElementById('custom-val-input');
                if (valInput && !valInput.disabled) {
                  valInput.focus();
                  try {
                    if (valInput.type === 'text' && valInput.value) {
                      const len = valInput.value.length;
                      valInput.setSelectionRange(len, len);
                    }
                  } catch (err) {}
                  return true;
                }
                return false;
              };

              if (!tryFocus()) {
                let attempts = 0;
                const interval = setInterval(() => {
                  if (tryFocus() || attempts > 20) {
                    clearInterval(interval);
                  }
                  attempts++;
                }, 20);
              }
            };

            if (valueSource === 'excel') {
              setValueSource('custom');
            }
            focusCustomInput();
          }
        }
        return;
      }

      if (isTextInputReal) {
        if (e.key !== 'Enter') return;
        // Let Shift+Enter insert newlines normally in textareas
        if (
          (activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable) &&
          e.shiftKey
        ) {
          return;
        }
      }

      if (e.key === 'Enter') {
        const isComboBox =
          activeEl &&
          (activeEl.getAttribute('role') === 'combobox' ||
            activeEl.classList?.contains('MuiSelect-select'));
        if (isComboBox) {
          return;
        }

        // If Enter is pressed on grid mapping for a Date, open calendar instead of submitting
        if (!isTextInputReal && !isControl && isDateInputRendered) {
          openPicker(e);
          return;
        }

        if (state.invalidMatchState?.type === 'TEXT') return;

        enterOriginRef.current = 'submit';

        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(true);
        return;
      }

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        // BREAK THE DATAGRID FOCUS TRAP BEFORE CHANGING COLUMNS
        if (activeEl && activeEl.tagName !== 'BODY') {
          activeEl.blur();
        }

        if (e.key === 'ArrowLeft' && navPrevRef.current) navPrevRef.current();
        if (e.key === 'ArrowRight' && navNextRef.current) navNextRef.current();
        if (e.key === 'ArrowUp' && navUpRef.current) navUpRef.current();
        if (e.key === 'ArrowDown' && navDownRef.current) navDownRef.current();
      }
    };

    const handleGlobalKeyUp = (e) => {
      if (!state.invalidMatchState?.open) return;

      if (e.key === 'Escape') {
        // Handled completely in KeyDown now to prevent double-firing
        return;
      }

      if (isDatePickerOpenRef.current) return;

      if (e.key === ' ' || e.code === 'Space') {
        if (spaceTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          spaceTriggeredRef.current = false;
          return;
        }
      }

      const activeEl = document.activeElement;

      if (activeEl && (activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable)) {
        if (e.key !== 'Enter' || e.shiftKey) return;
      } else if (activeEl && activeEl.tagName === 'INPUT' && e.key !== 'Enter') {
        return;
      }

      if (e.key === 'Enter') {
        const origin = enterOriginRef.current;
        enterOriginRef.current = null;

        if (origin === 'listbox') {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            if (submitRef.current) submitRef.current();
          }, 150);
          return;
        }

        if (origin !== 'submit') return;

        e.preventDefault();
        e.stopPropagation();

        setIsOkPressed(false);
        if (submitRef.current) {
          submitRef.current();
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    window.addEventListener('keyup', handleGlobalKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
      window.removeEventListener('keyup', handleGlobalKeyUp, { capture: true });
    };
  }, [
    state.invalidMatchState,
    assignedTo,
    selection,
    valueSource,
    pendingMappingValue,
    sortedIcons,
    state.markerIcons,
    restoreGridFocus,
    openPicker,
    forceCalendarFocus,
  ]);

  return (
    <Box
      id='assign-property-panel'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 2,
        pl: 3,
        pr: '52px',
      }}>
      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
        {showThreeTierLayout
          ? `Assign Property Column: ${makeUppercase && typeof state.invalidMatchState?.colName === 'string' ? state.invalidMatchState.colName.toUpperCase() : state.invalidMatchState?.colName}`
          : `Assign ${state.invalidMatchState?.type === 'REGION' ? 'Region' : state.invalidMatchState?.type === 'CATEGORY' ? 'Category' : 'Icon'}`}
      </Typography>

      <AssignPropertyContent
        state={state}
        makeUppercase={makeUppercase}
        assignedTo={assignedTo}
        setAssignedTo={setAssignedTo}
        selection={selection}
        setSelection={setSelection}
        valueSource={valueSource}
        setValueSource={setValueSource}
        staticValue={staticValue}
        setStaticValue={setStaticValue}
        pendingMappingValue={pendingMappingValue}
        setPendingMappingValue={setPendingMappingValue}
        sortedIcons={sortedIcons}
        isDatePickerOpen={isDatePickerOpen}
        isDatePickerOpenRef={isDatePickerOpenRef}
        openPicker={openPicker}
        closePicker={closePicker}
        onSubmit={() => {
          if (submitRef.current) submitRef.current();
        }}
      />

      <DialogActionBar
        applyToAllChecked={applyToAllRows}
        onApplyToAllChange={(e) => setApplyToAllRows(e.target.checked)}
        disableApplyToAll={isUnspecified}
        onCancel={handleValidationCancelClick}
        disableCancel={false}
        onUp={handleNavUp}
        onDown={handleNavDown}
        onPrev={handleNavPrev}
        onNext={handleNavNext}
        disableNav={false}
        onOk={() => handleValidationOkSubmit(false)}
        disableOk={isOkDisabled}
        isOkPressed={isOkPressed}
        isLoadingOk={false}
      />
    </Box>
  );
}
